import React, { useEffect, useRef } from "react";
import Highcharts from "highcharts";
import {  
  getColorFromStatus,
  processData,
} from "../../utility/utility";
import { Card } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import HighchartsReact from "highcharts-react-official";
import HCMap from "highcharts/modules/map";

HCMap(Highcharts);

interface ChartData {
  name: string;
  y: number;
}

interface ConsentStats {
  consent_stats_per_subject: Record<string, Record<string, string>>;
}

interface ConcentStatesProps {
  title: string;
  data: ChartData[];
  counts: number;
  id: number;
  customLegendId: string;
  subtitleId: string;
}

interface ProcessedChartData {
  title: string;
  data: ChartData[];
  totalSum: number;
}

interface DonutChartProps {
  consentStats: ConsentStats;
  customLegendId: string;
  subtitleId: string;
}

const ConsentStates: React.FC<ConcentStatesProps> = (props) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null); // For Custom Legends
  const total = props?.data?.reduce((acc, point) => acc + point.y, 0); // Calculate total

  const chartOptions = {
    chart: {
      type: "pie",
      height: 150,
      margin: [0, 0, 0, 0],
      events: {
        load: function () {
          createCustomLegend(props?.id);
        },
      },
    },
    title: {
      text: null, 
    },
    credits: {
      enabled: false, 
    },
    plotOptions: {
      pie: {
        innerSize: "75%",
        dataLabels: {
          enabled: false, // Disable data labels
        },
        size: "100%",
      },
    },
    series: [
      {
        name: "Number",
        colorByPoint: true,
        data: props?.data,
      },
    ],
  };

  useEffect(() => {
    createCustomLegend(props?.id);
  }, [props?.data]);

  const createCustomLegend = (id: number) => {
    const customLegend = document.getElementById(`customLegendId_${id}`);
    const chart = chartRef?.current?.chart; // Access the correct chart instance

    const desiredOrder = ["Pending", "Approved", "Rejected", "Self", "Revoked Ap.", "Revoked Re."]; // Replace with your actual statuses

    if (customLegend && chart) {
      customLegend.innerHTML = ""; // Clear existing legend items

      const sortedData = chart.series[0].data.sort((a: any, b: any) => {
        return desiredOrder.indexOf(a.name) - desiredOrder.indexOf(b.name);
      });

      sortedData.forEach((point: any) => {
        const percentage = ((point?.y / total) * 100).toFixed(2); // Calculate percentage
        const legendItem = document.createElement("div");
        legendItem.className = "legend-item";
        legendItem.innerHTML = `
        <span style="color:${getColorFromStatus(point.name)}; font-size:15px; display:inline-block; width:16px; height:10px; border-radius:4px; background-color:${getColorFromStatus(point.name)};"></span>
        <span style="color:#211F24; font-size:14px; font-weight:600;">${percentage}%</span> 
        <span style="color:#909090; font-size:14px;">${point.name}</span>`;

        legendItem.onclick = function () {
          point.selected = !point.selected; // Toggle the selected property
          chart.series[0].chart.redraw(); // Redraw the chart to reflect changes
        };

        customLegend.appendChild(legendItem); // Append the legend item
      });

      // Update the legend subtitle with the total
      const legendSubtitleElement = document.getElementById(`subtitleId_${id}`);
      if (legendSubtitleElement) {
        legendSubtitleElement.innerHTML = `${total}`;
      }
    }
  };

  return (
    <div className="donut-chart-wrapper">
      <div className="chart-part">
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          ref={chartRef}
        />
      </div>
      <div className="donut-chart-legend-part">
        <div className="donut-chart-header-part">
          <div className="legend-subtitle" id={`subtitleId_${props?.id}`}>
            {props?.counts}
          </div>
        </div>
        <div className="custom-legend" id={`customLegendId_${props?.id}`}>
          {/* Custom legend items will be populated here */}
        </div>
      </div>
    </div>
  );
};

const ConsentChildStats: React.FC<DonutChartProps> = (props) => {
  const chartsData: ProcessedChartData[] =
    props?.consentStats &&
    processData(props?.consentStats?.consent_stats_per_subject);

  return (
    <>
      {props?.consentStats || chartsData?.length > 0 ? (
        chartsData.map((chart, index) => (
          <Card key={index}>
            <Card.Body>
              <div className="legend-title">{chart.title}</div>
              <ConsentStates
                title={chart.title}
                data={chart.data}
                counts={chart.totalSum}
                id={index}
                customLegendId={props?.customLegendId}
                subtitleId={props?.subtitleId}
              />
            </Card.Body>
          </Card>
        ))
      ) : (
        <Card.Body>
          <Skeleton count={15} />
        </Card.Body>
      )}
    </>
  );
};

export default ConsentChildStats;
